/**
 * Launcher for Desktop/Mobile 3rdParty games
 */

define(function(require, exports, module) {
    'use strict';

    const _ = require('underscore');
    const core = require('core');
    const i18n = require('i18n');
    const user = require('user');
    const config = require('config');
    const url = require('util/url.util');
    const device = require('util/device.util');
    const storage = require('util/storage.util');
    const language = require('util/language.util');
    const historyUtil = require('util/history.util');
    const LauncherWindowView = require('modules/launcher/launcher.window.view');

    module.exports = {

        /**
         * Detects formFactor
         * @return {string}
         */
        get formFactor() {
            return !device.isDesktop ? 'mobile' : 'desktop';
        },

        setLauncher: function(launcher) {
            this.launcher = launcher;
        },

        setAdditionalQueryString: function(queryString) {
            this.additionalQueryString = queryString;
        },

        /**
         * Open 3rdParty game
         * @param {Object} game
         * @param {number} mode
         */
        open: function(game, mode) {
            // singular changes between desktop & mobile platform
            this.provider = game.get(!device.isDesktop ? 'mobileGameType' : 'desktopGameType');
            const launchMethod = !device.isDesktop
                ? this.getLaunchOptions().launchMethodMobile
                : this.getLaunchOptions().launchMethodDesktop;
            var isRedirectInNewWindow = launchMethod === 'redirectInNewWindow';
            var gameWindow = isRedirectInNewWindow ? url.openNewWindow() : null;

            if (launchMethod === 'wrapperInThisWindow' || launchMethod === 'wrapperInNewWindow') {
                this.launch({
                    provider: this.provider,
                    game: game,
                    mode: mode,
                    gameWindow: gameWindow,
                    urlType: this.getTypeUrl(mode)?.[0]
                });

                return;
            }

            window.Playtech.API.server.getURLTemplate({
                urlTypeList: this.getTypeUrl(mode),
                clientPlatform: !device.isDesktop ? 'mobile' : 'web'
            })
                .then((launchUrl) => {
                    this.successUrlTemplateLaunch(launchUrl, gameWindow, game, mode);
                })
                .catch(() => {
                    this.failedUrlTemplateLaunch(gameWindow, game);
                });
        },

        /**
         * Get typeUrl which depends on game mode and preview mode
         * @param {number} mode
         * @return {Array} array with url type
         */
        getTypeUrl: function(mode) {
            const gameType = _.findWhere(config.get('gameTypes'), {name: this.provider});

            if (!gameType) {
                return [];
            }

            const isPreviewMode = Number(url.getLocationParameterByName('preview')) === 1;
            var typeUrl;

            if (isPreviewMode) {
                typeUrl = gameType[mode ? 'previewRealPlayUrl' : 'previewDemoPlayUrl'];
            }

            if (!isPreviewMode || !typeUrl) {
                typeUrl = gameType[mode ? 'realPlayUrl' : 'demoPlayUrl'];
            }

            return [typeUrl];
        },

        /**
         * Show error message and close game window if game launch failed
         */
        failedUrlTemplateLaunch: function(gameWindow, game) {
            if (gameWindow) {
                gameWindow.close();
            }

            window.Playtech.API.popup.showPopup({
                title: game.get('name') + ' ' + i18n.t('playtech.mobile.system.launcher.gameUnavailablePopup.title'),
                content: i18n.t('playtech.mobile.system.launcher.gameUnavailablePopup.desktop-content'),
                onClose: () => this.navigateFromLaunchGamePath()
            });
        },

        /**
         * Launch game after success getURLsRequest
         */
        successUrlTemplateLaunch: async function(launchUrl, gameWindow, game, mode) {
            if (!launchUrl) {
                this.failedUrlTemplateLaunch(gameWindow, game);
                return;
            }

            launchUrl = this.prepareLaunchUrl(launchUrl, game);

            try {
                launchUrl = await this.prepareToken(launchUrl);
            } catch (e) {
                this.failedUrlTemplateLaunch(gameWindow, game);
                return;
            }

            this.launch({
                provider: this.provider,
                game: game,
                mode: mode,
                launchUrl: launchUrl,
                gameWindow: gameWindow
            });
        },

        /**
         * Open game with right launch method
         * @param  {Object} options
         */
        launch: function(options) {
            const preview = url.getLocationParameterByName('preview');

            options = _.extend(options, this.getLaunchOptions());

            if (options.launchUrl && preview != null) {
                options.launchUrl += (options.launchUrl.includes('?') ? '&' : '?') +
                    'preview=' + preview;
            }

            var launchMethod = !device.isDesktop
                ? options.launchMethodMobile
                : options.launchMethodDesktop;

            switch (launchMethod) {
                case 'redirectInThisWindow':
                    this.redirectLaunch(this.prepareRedirectUrl(options));
                    break;
                case 'wrapperInThisWindow':
                    this.redirectLaunch(this.prepareWrapperUrl(options));
                    break;
                case 'redirectInNewWindow':
                    this.launchInNewWindow(options, 'redirect');
                    break;
                case 'wrapperInNewWindow':
                    this.launchInNewWindow(options, 'wrapper');
                    break;
                default:
                    this.redirectLaunch(this.prepareRedirectUrl(options));
            }
        },

        /**
         * Get configured launch method
         * @param {string} [gameType]
         * @return {string}
         */
        getLaunchOptions: function(gameType) {
            return _.find(config.get('gameTypes'), (item) => item.name === (gameType || this.provider));
        },

        /**
         * Replace [params] in launch url
         * @return {string}
         */
        prepareLaunchUrl: function(launchUrl, game) {
            return launchUrl
                .replace(/\[superdomain]/g, window.location.hostname.split('.').slice(1).join('.'))
                .replace(/\[mobilegamecode]/g, game.get('mobileGameCode') || game.get('gameCode'))
                .replace(/\[gamecode]/g, game.get('gameCode'))
                .replace('[gamename]', game.get('name'))
                .replace('[language]', language.getTwoLetterLanguage(user.get('lang')))
                .replace('[back_url]', url.origin)
                .replace('[integrationtype]', game.get('integrationType') || '')
                .replace('[livelaunchalias]', game.get('liveLaunchAlias') || '');
        },

        /**
         * Prepare launch URL for redirect
         * @param  {Object} options
         * @return {Object}
         */
        prepareRedirectUrl: function(options) {
            let redirectUrl = options.launchUrl;

            redirectUrl = this.addUrlCustomParameters(redirectUrl, options); // add query string from CMS game config
            redirectUrl = this.getGenericLaunchUrl(redirectUrl); // add custom query string from URL
            redirectUrl = this.addAdditionalParamsToLaunchUrl(redirectUrl); // add query string from portlet config (have more priority)

            return {
                ...options,
                launchUrl: redirectUrl.toString()
            };
        },

        /**
         * Prepare launch URL for wrapper
         * @param  {Object} options
         * @return {Object}
         */
        prepareWrapperUrl: function(options) {
            const preview = url.getLocationParameterByName('preview') || '';
            const requestStamp = config.get('requestStamp');
            const wrapperUrl = new window.URL(`${url.origin}/${config.get('themeName')}/js/app-out/html/wrapper.html`);
            const gameLaunchType = !device.isDesktop ? options.game.get('mobileGameType') : options.game.get('desktopGameType');

            wrapperUrl.searchParams.append('urlType', options.urlType);
            wrapperUrl.searchParams.append('preview', preview);
            wrapperUrl.searchParams.append('additionalQueryString', this.additionalQueryString || '');
            wrapperUrl.searchParams.append('t', requestStamp);

            if (options?.game) {
                const gameName = options.game.get('name') || '';
                const gameCode = options.game.get('gameCode') || '';
                const mobileGameCode = options.game.get('mobileGameCode') || '';
                const gameType = gameLaunchType || '';
                const integrationType = options.game.get('integrationType') || '';
                const liveLaunchAlias = options.game.get('liveLaunchAlias') || '';
                const urlCustomParameters = options.game.get('urlCustomParameters') || '';

                wrapperUrl.searchParams.append('gameName', gameName);
                wrapperUrl.searchParams.append('gameType', gameType);
                wrapperUrl.searchParams.append('game', gameCode);
                wrapperUrl.searchParams.append('gameCode', gameCode);
                wrapperUrl.searchParams.append('mobileGameCode', mobileGameCode || gameCode);
                wrapperUrl.searchParams.append('integrationType', integrationType);
                wrapperUrl.searchParams.append('liveLaunchAlias', liveLaunchAlias);
                wrapperUrl.searchParams.append('urlCustomParameters', urlCustomParameters);
            }

            return {
                ...options,
                launchUrl: wrapperUrl.toString()
            };
        },

        prepareToken: async function(launchUrl) {
            if (!launchUrl.includes('[temptoken]')) {
                return launchUrl;
            }

            const token = await window.Playtech.API.auth.getTemporaryAuthenticationToken();

            return launchUrl.replace('[temptoken]', token);
        },

        /**
         * [launchInNewWindow Open game in new window by redirect]
         * @param  {Object} [options]
         * @param  {string} [type]    [Method Launch Type]
         */
        launchInNewWindow: function(options, type) {
            const launchOptions = type === 'wrapper'
                ? this.prepareWrapperUrl(options)
                : this.prepareRedirectUrl(options);

            new LauncherWindowView(launchOptions);

            if (url.isLaunchGamePath()) {
                this.navigateFromLaunchGamePath();
            }
        },

        /**
         * Open game in current window
         * @param  {Object} options
         */
        redirectLaunch: function(options) {
            if (url.pathname.includes('/igaming/')) {
                historyUtil.replaceState(null, null, config.get('rootPage') || '/');
            }

            url.redirect(options.launchUrl);
        },

        addUrlCustomParameters: function(launchUrl, options) {
            if (!options?.game) {
                return launchUrl;
            }

            const gameName = options.game.get('name');
            const urlCustomParameters = options.game.get('urlCustomParameters');

            if (!urlCustomParameters) {
                return launchUrl;
            }

            try {
                const resultUrl = new window.URL(launchUrl);
                const additionalUrlCustomParameters = new window.URLSearchParams(urlCustomParameters);

                additionalUrlCustomParameters.forEach((value, key) => {
                    resultUrl.searchParams.set(key, value);
                });

                return resultUrl.toString();
            } catch {
                core.error(`Game configuration error: check urlCustomParameters for game "${gameName}"`);
            }

            return launchUrl;
        },

        /**
         * Generic URL using configured URL for game provider & added all url params to game launch URL.
         * @param launchUrl
         * @returns {string}
         */
        getGenericLaunchUrl: function(launchUrl) {
            try {
                const resultUrl = new window.URL(launchUrl);
                const locationUrlParameters = new window.URLSearchParams(window.location.search);

                locationUrlParameters.forEach((value, key) => {
                    if (key === 'real' || key === 'back_url') {
                        return;
                    }

                    resultUrl.searchParams.set(key, value);
                });

                return resultUrl.toString();
            } catch {
                return launchUrl;
            }
        },

        /**
         * Add additional URL parameters to the end of launch URl, existing parameters will not changed
         * @param launchUrl
         * @return {string}
         */
        addAdditionalParamsToLaunchUrl: function(launchUrl) {
            if (!this.additionalQueryString) {
                return launchUrl;
            }

            try {
                const resultUrl = new window.URL(launchUrl);
                const additionalQueryStringParameters = new window.URLSearchParams(this.additionalQueryString);

                additionalQueryStringParameters.forEach((value, key) => {
                    resultUrl.searchParams.set(key, value);
                });

                return resultUrl.toString();
            } catch {
                return launchUrl;
            }
        },

        navigateFromLaunchGamePath: function() {
            const gamesPageUrl = storage.session.getItem('gamesPageUrl');
            const { prevPath } = historyUtil.state?.state || {};

            if (url.isLaunchGamePath()) {
                storage.session.removeItem('gamesPageUrl');

                Playtech.API.common.replace(gamesPageUrl || prevPath || config.get('rootPage'));
            }
        }
    };
});
